import { format, parseISO } from 'date-fns';
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type {
  DSRTrendPoint,
  SafetyEventTrendPoint,
  SSETrendPoint,
  TopActionItem,
} from '@/services/db/analytics';

// Mozaic palette (Tailwind tokens not available inside SVG fills).
const COLOR = {
  red: '#dc2626',
  amber: '#f59e0b',
  green: '#16a34a',
  blue: '#2563eb',
  teal: '#14b8a6',
  muted: '#6b7280',
};

interface ChartCardProps {
  title: string;
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  onRetry?: () => void;
  children: React.ReactNode;
}

export function ChartCard({
  title, isLoading, isError, isEmpty, emptyMessage, onRetry, children,
}: ChartCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[220px] w-full" />
        ) : isError ? (
          <div className="flex h-[220px] flex-col items-center justify-center gap-2">
            <p className="text-sm text-muted-foreground">Unable to load data</p>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
            )}
          </div>
        ) : isEmpty ? (
          <div className="flex h-[220px] items-center justify-center">
            <p className="text-sm text-muted-foreground">{emptyMessage ?? 'No data'}</p>
          </div>
        ) : (
          <div className="w-full" style={{ minHeight: 220 }}>{children}</div>
        )}
      </CardContent>
    </Card>
  );
}

const fmtDay = (d: string) => {
  try { return format(parseISO(d), 'MMM d'); } catch { return d; }
};

// ── Daily Risk stacked bar ──────────────────────────────────

export function DailyRiskChart({ data }: { data: DSRTrendPoint[] }) {
  return (
    <ResponsiveContainer width="100%" height={220} minHeight={220}>
      <BarChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="date" tickFormatter={fmtDay} fontSize={11} />
        <YAxis allowDecimals={false} fontSize={11} />
        <Tooltip
          labelFormatter={fmtDay as any}
          formatter={((value: number, name: string) => [value, name]) as any}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="low_risk_count"    name="Low"    stackId="r" fill={COLOR.green} />
        <Bar dataKey="medium_risk_count" name="Medium" stackId="r" fill={COLOR.amber} />
        <Bar dataKey="high_risk_count"   name="High"   stackId="r" fill={COLOR.red} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Safety event line chart ─────────────────────────────────

export function SafetyEventLineChart({ data }: { data: SafetyEventTrendPoint[] }) {
  return (
    <ResponsiveContainer width="100%" height={220} minHeight={220}>
      <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="date" tickFormatter={fmtDay} fontSize={11} />
        <YAxis allowDecimals={false} fontSize={11} />
        <Tooltip labelFormatter={fmtDay as any} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line type="monotone" dataKey="near_miss_count"  name="Near Miss"   stroke={COLOR.amber} dot={false} />
        <Line type="monotone" dataKey="first_aid_count"  name="First Aid"   stroke={COLOR.blue}  dot={false} />
        <Line type="monotone" dataKey="good_catch_count" name="Good Catches" stroke={COLOR.green} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── SSE area chart ──────────────────────────────────────────

export function SSEAreaChart({ data }: { data: SSETrendPoint[] }) {
  // Series-level color is fixed; spec asks for amber > 15% / green ≤ 15%.
  // We pick the dominant color based on how many points exceed threshold.
  const overCount = data.filter((d) => d.sse_percentage > 15).length;
  const fill = overCount > data.length / 2 ? COLOR.amber : COLOR.green;
  return (
    <ResponsiveContainer width="100%" height={220} minHeight={220}>
      <AreaChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="sseGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={fill} stopOpacity={0.4} />
            <stop offset="100%" stopColor={fill} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="date" tickFormatter={fmtDay} fontSize={11} />
        <YAxis fontSize={11} unit="%" />
        <Tooltip
          labelFormatter={fmtDay as any}
          formatter={((v: number, name: string) =>
            name === 'SSE %' ? [`${v}%`, name] : [v, name]) as any}
        />
        <ReferenceLine y={15} stroke={COLOR.muted} strokeDasharray="4 4" label={{ value: '15%', fontSize: 10, fill: COLOR.muted }} />
        <Area type="monotone" dataKey="sse_percentage" name="SSE %" stroke={fill} fill="url(#sseGrad)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ── Top action items list ───────────────────────────────────

const PRIORITY_BADGE: Record<string, string> = {
  critical: 'bg-mozaic-red/20 text-mozaic-red font-semibold',
  high:     'bg-mozaic-red/15 text-mozaic-red',
  medium:   'bg-mozaic-orange/15 text-mozaic-orange',
  low:      'bg-mozaic-green/15 text-mozaic-green',
};

export function TopActionItemsCard({
  items, isLoading, isError, onViewAll,
}: {
  items: TopActionItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onViewAll?: () => void;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm">Open Action Items</CardTitle>
        {onViewAll && (
          <Button variant="link" size="sm" className="h-auto p-0 text-xs text-mozaic-teal" onClick={onViewAll}>
            View all &rarr;
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">Unable to load data</p>
        ) : !items || items.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No open action items
          </p>
        ) : (
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.id} className="flex items-start gap-3 border-b border-border/50 pb-2 last:border-0">
                <Badge className={PRIORITY_BADGE[it.priority] ?? PRIORITY_BADGE.medium}>
                  {it.priority}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{it.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {it.due_date ? `Due ${format(parseISO(it.due_date), 'MMM d')}` : 'No due date'}
                    {it.source_section && ` · ${it.source_section}`}
                    {it.project_name && ` · ${it.project_name}`}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ── Hook to bind a query result to ChartCard ────────────────

export interface QueryLike<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useChartState<T>(q: QueryLike<T[]>, emptyMessage: string) {
  const isEmpty = !q.isLoading && !q.isError && (!q.data || q.data.length === 0);
  return {
    isLoading: q.isLoading,
    isError: q.isError,
    isEmpty,
    emptyMessage,
    onRetry: q.refetch,
  };
}

